import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { API_URL } from './config/apiConfig';
import { TutorialMessageBox } from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import { playSoundEffect } from './hooks/useSoundEffects';
import WarEffortSkeletonCrewSelection, { SKELETON_CREW_BG_STYLE } from './WarEffortSkeletonCrewSelection';
import './WarEffortPurposeSelection.css';

const PURPOSES = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];

const PURPOSE_TO_FACTION_MAPPING = {
  'Consume': ['Asrah', 'The Hollow', 'The Forsaken Pact'],
  'Eradicate': ['Exolot Dominion', 'The Corrupted Core', 'The Iron Alliance'],
  'Colonize': ['League of Shadows', 'The Conspiracy', 'The Shadow Consortium'],
  'Protect': ['Ra\'s Children', 'The Divine Syndicate', 'The Covenant'],
  'Survive': ['Remnants of Humanity', 'The Last Crusade', 'The Silent Accord']
};

const PURPOSE_IMAGES = {
  'Consume': 'asrah_ground.jpg',
  'Eradicate': 'exolot_landing.jpg',
  'Colonize': 'league_portal.jpg',
  'Protect': 'ra_seat.jpg',
  'Survive': 'remnants_headquarter.jpg'
};

const PURPOSE_DESCRIPTIONS = {
  'Consume': 'All that exists is the spread of Asrah\'s essence. It is not a choice—only the inevitable continuation of a hunger older than memory. Worlds fall, civilizations vanish, and still the mission persists without end or mercy. Earth is simply the next resistance to be dissolved. Advance toward the enemy\'s strongholds and swallow their leadership from within, piece by piece, until silence is the only thing left.',
  'Eradicate': 'Asrah was defeated once, but the victory carved too deep a wound to forget. The cost was unbearable, and the shadow of that sacrifice still haunts every step forward. Allowing Asrah to rise again is impossible; allowing him to escape justice is unforgivable. No ally, no obstacle, no innocent will change the course now. The debt must be paid in full. The corruption will end—permanently.',
  'Colonize': 'The opening of the portals revealed a world unguarded and unprepared. The Shadowverse did not request entry, nor did it require permission. Its denizens stepped forward driven by curiosity and by the quiet, unshakable belief that this place—Earth—was theirs by right. A new frontier, ripe for reshaping. Claim it, bend it, and establish dominion before anyone else can stake a claim.',
  'Protect': 'Earth remains flawed, fragile, and ungrateful, yet bound to you by ancient ties that time has failed to sever. Humanity has forgotten its benefactors, but the oath once spoken still stands. In their darkest hour—when gods are a myth and hope is a rumor—you return once more. Not for recognition. Not for praise. But because protection is the last obligation left in a world slipping toward oblivion.',
  'Survive': 'Humanity stands at the edge of extinction. Their weapons fail, their leaders fall, and Asrah advances as an unstoppable tide. Surrender is logical, but never accepted. Instead, the remnants gather in ruined shelters and burning streets, stitching together desperate plans. Every battle is hopeless. Every victory is borrowed time. Yet they fight, not for triumph, but because survival is the final rebellion the species can offer.'
};

// Faction orb background (same logic as Strategy.js)
function getFactionOrbBackground(factionName) {
  if (!factionName || factionName === 'Unknown') return '#ccc';
  if (factionName === 'The Conspiracy') return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  if (factionName === 'The Corrupted Core') return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  if (factionName === 'The Covenant') return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  if (factionName === 'The Divine Syndicate') return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  if (factionName === 'The Forsaken Pact') return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  if (factionName === 'The Hollow') return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  if (factionName === 'The Iron Alliance') return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  if (factionName === 'The Last Crusade') return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  if (factionName === 'The Shadow Consortium') return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  if (factionName === 'The Silent Accord') return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    "Ra's Children": '#F4A460',
    'Asrah': '#8B0000'
  };
  return factionColors[factionName] || '#ccc';
}

function WarEffortPurposeSelection({
  user,
  onPurposeSelected,
  onSkeletonCrewActiveChange,
  mission = null,
  onMissionUpdate,
  onInviteAlly,
  inviteAllyLoading = false,
  inviteAllyError = null,
  friends = [],
  isPlayer1 = false,
  onDevSimulate2v2Join = null,
  devSimulateLoading = false,
  onDevResetToWaitingAlly = null,
  devResetLoading = false,
  onReady = null,
  onStartCrewSelection = null,
  onStartFFAGame = null,
  isDevUser = false
}) {
  const { t, i18n } = useTranslation();
  const [missionMode, setMissionMode] = useState(mission?.mission_mode === 'ffa' ? 'ffa' : (mission?.mission_mode === '2v2' ? '2v2' : '1v1'));
  const [selectedChallenge, setSelectedChallenge] = useState('hard'); // 'easy' | 'hard' for neutral country bot strategies
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [selectedFaction, setSelectedFaction] = useState(null);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTutorialPurposeSelection, setShowTutorialPurposeSelection] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  const [pendingSkeletonCrew, setPendingSkeletonCrew] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [showAllyPicklist, setShowAllyPicklist] = useState(false);
  const allyPicklistRef = useRef(null);
  const [openFFAMissions, setOpenFFAMissions] = useState([]);
  const [openFFALoading, setOpenFFALoading] = useState(false);
  const [joinFFALoading, setJoinFFALoading] = useState(false);
  const [setFFAPurposeLoading, setSetFFAPurposeLoading] = useState(false);
  const [assignBotLoading, setAssignBotLoading] = useState(false);

  const localUserId = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        if (decoded.userId != null) return Number(decoded.userId);
      }
    } catch {}
    const ls = localStorage.getItem('userId');
    return ls != null ? Number(ls) : null;
  }, []);
  const isMissionCreator = mission?.player1_id != null && localUserId != null && Number(mission.player1_id) === localUserId;
  const effectiveIsPlayer1 = isPlayer1 || isMissionCreator;

  const hasExisting2v2Mission = mission?.mission_mode === '2v2' && ['waiting_ally', 'waiting_opponents', 'waiting_opponent_ally'].includes(mission?.status);
  const hasExistingFFAMission = mission?.mission_mode === 'ffa' && mission?.status === 'waiting_players';

  // FFA: which slot is the current user (1-4)? Used to show only mission data per slot and to call set-ffa-purpose for own slot.
  const ffaMySlot = useMemo(() => {
    if (!hasExistingFFAMission || !mission || localUserId == null) return null;
    if (Number(mission.player1_id) === localUserId) return 1;
    if (Number(mission.player2_id) === localUserId) return 2;
    if (Number(mission.player3_id) === localUserId) return 3;
    if (Number(mission.player4_id) === localUserId) return 4;
    return null;
  }, [hasExistingFFAMission, mission, localUserId]);
  const ffaMyPurpose = ffaMySlot === 1 ? mission?.player1_purpose : ffaMySlot === 2 ? mission?.player2_purpose : ffaMySlot === 3 ? mission?.player3_purpose : ffaMySlot === 4 ? mission?.player4_purpose : null;
  const ffaMyFactionName = ffaMySlot === 1 ? mission?.player1_faction_name : ffaMySlot === 2 ? mission?.player2_faction_name : ffaMySlot === 3 ? mission?.player3_faction_name : ffaMySlot === 4 ? mission?.player4_faction_name : null;
  const ffaMyStartedAt = ffaMySlot === 1 ? mission?.player1_started_at : ffaMySlot === 2 ? mission?.player2_started_at : ffaMySlot === 3 ? mission?.player3_started_at : ffaMySlot === 4 ? mission?.player4_started_at : null;
  const canPickAlly = hasExisting2v2Mission && mission?.status === 'waiting_ally' && effectiveIsPlayer1;

  const allSlotsFilledIn2v2 = hasExisting2v2Mission && mission?.player1_id && mission?.player2_id && mission?.player3_id && mission?.player4_id;
  // Same as 2v2: "all ready" = every filled slot has ready; "viewer ready" = current user's slot is ready
  const allReady2v2 = allSlotsFilledIn2v2 && mission?.player1_ready && mission?.player2_ready && mission?.player3_ready && mission?.player4_ready;
  const allReadyFFA = hasExistingFFAMission && (!mission?.player1_id || mission?.player1_ready) && (!mission?.player2_id || mission?.player2_ready) && (!mission?.player3_id || mission?.player3_ready) && (!mission?.player4_id || mission?.player4_ready);
  const allReady = allReady2v2 || allReadyFFA;
  const ffaFirstEmptySlot = hasExistingFFAMission ? (!mission?.player2_id ? 2 : !mission?.player3_id ? 3 : !mission?.player4_id ? 4 : null) : null;
  const ffaHasBot = hasExistingFFAMission && (mission?.player2_is_bot || mission?.player3_is_bot || mission?.player4_is_bot);
  const showAddBot = hasExistingFFAMission && ffaFirstEmptySlot != null && !ffaHasBot;
  const viewerIsReady2v2 = effectiveIsPlayer1 ? mission?.player1_ready : false;
  const viewerIsReadyFFA = ffaMySlot === 1 ? mission?.player1_ready : ffaMySlot === 2 ? mission?.player2_ready : ffaMySlot === 3 ? mission?.player3_ready : ffaMySlot === 4 ? mission?.player4_ready : false;
  const viewerIsReady = (hasExisting2v2Mission && viewerIsReady2v2) || (hasExistingFFAMission && viewerIsReadyFFA);
  const showReadySection = (hasExisting2v2Mission && allSlotsFilledIn2v2 && !allReady2v2) || (hasExistingFFAMission && ffaMySlot != null && !allReadyFFA);

  // Fetch pending skeleton crew (locked until used past cease fire)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setPendingLoading(false); return; }
    fetch(`${API_URL}/war-effort/pending-skeleton-crew`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : { hasPending: false })
      .then(data => {
        if (data.hasPending && data.purpose && data.selectedFaction) {
          setPendingSkeletonCrew({ purpose: data.purpose, selectedFaction: data.selectedFaction });
        } else {
          setPendingSkeletonCrew(null);
        }
      })
      .catch(() => setPendingSkeletonCrew(null))
      .finally(() => setPendingLoading(false));
  }, []);

  // Sync missionMode when we have an existing 2v2 or FFA mission
  useEffect(() => {
    if (mission?.mission_mode === '2v2') setMissionMode('2v2');
    if (mission?.mission_mode === 'ffa') setMissionMode('ffa');
  }, [mission?.mission_mode]);

  // FFA lobby: sync purpose/faction bar from my slot so it shows current choice
  useEffect(() => {
    if (hasExistingFFAMission && ffaMySlot != null) {
      if (ffaMyPurpose) setSelectedPurpose(ffaMyPurpose);
      if (ffaMyFactionName) setSelectedFaction(ffaMyFactionName);
    }
  }, [hasExistingFFAMission, ffaMySlot, ffaMyPurpose, ffaMyFactionName]);

  // Fetch open FFA missions when in FFA mode and not already in an FFA mission; refresh every 15s
  useEffect(() => {
    if (missionMode !== 'ffa' || hasExistingFFAMission) {
      setOpenFFAMissions([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchOpenFFA = (showLoading = false) => {
      if (showLoading) setOpenFFALoading(true);
      fetch(`${API_URL}/war-effort/open-ffa-missions`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : { missions: [] })
        .then(data => setOpenFFAMissions(Array.isArray(data.missions) ? data.missions : []))
        .catch(() => setOpenFFAMissions([]))
        .finally(() => { if (showLoading) setOpenFFALoading(false); });
    };

    fetchOpenFFA(true);
    const intervalId = setInterval(() => fetchOpenFFA(false), 15000);
    return () => clearInterval(intervalId);
  }, [missionMode, hasExistingFFAMission]);

  const handleRefreshOpenFFA = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setOpenFFALoading(true);
    fetch(`${API_URL}/war-effort/open-ffa-missions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : { missions: [] })
      .then(data => setOpenFFAMissions(Array.isArray(data.missions) ? data.missions : []))
      .catch(() => setOpenFFAMissions([]))
      .finally(() => setOpenFFALoading(false));
  }, []);

  // Close ally picklist when clicking outside
  useEffect(() => {
    if (!showAllyPicklist) return;
    const handleClick = (e) => {
      if (allyPicklistRef.current && !allyPicklistRef.current.contains(e.target)) setShowAllyPicklist(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showAllyPicklist]);

  useEffect(() => {
    if (user && shouldShowTutorial('war_effort_purpose_selection')) {
      const timer = setTimeout(() => setShowTutorialPurposeSelection(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, shouldShowTutorial]);

  const handlePurposeSelect = (purpose) => {
    playSoundEffect('/sounds/click.mp3');
    setSelectedPurpose(purpose);
    setSelectedFaction(null);
    setError(null);
  };

  const handleSkeletonCrewComplete = useCallback(async (skeletonCrewOrderIds) => {
    if (!selectedPurpose || !selectedFaction) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/select-purpose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purpose: selectedPurpose,
          selectedFaction,
          skeletonCrewOrderIds,
          missionMode: missionMode || '1v1',
          neutralChallenge: selectedChallenge
        })
      });
      if (response.ok) {
        const mission = await response.json();
        onPurposeSelected(mission);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('warEffort.purposeSelection.failedToStartMission'));
      }
    } catch (err) {
      console.error('Error starting mission:', err);
      setError(t('warEffort.purposeSelection.failedToStartMission'));
    } finally {
      setLoading(false);
    }
  }, [selectedPurpose, selectedFaction, missionMode, selectedChallenge, onPurposeSelected]);

  // 2v2: create mission with purpose + faction only; skeleton crew is submitted later by each player
  const handleCreate2v2Mission = useCallback(async () => {
    if (!selectedPurpose || !selectedFaction) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/select-purpose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purpose: selectedPurpose,
          selectedFaction,
          missionMode: '2v2',
          neutralChallenge: selectedChallenge
        })
      });
      if (response.ok) {
        const mission = await response.json();
        onPurposeSelected(mission);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('warEffort.purposeSelection.failedToCreate2v2'));
      }
    } catch (err) {
      console.error('Error creating 2v2 mission:', err);
        setError(t('warEffort.purposeSelection.failedToCreate2v2'));
    } finally {
      setLoading(false);
    }
  }, [selectedPurpose, selectedFaction, selectedChallenge, onPurposeSelected]);

  // FFA: join an open mission (first empty slot). Purpose/faction optional; can set in lobby after joining.
  const handleJoinFFA = useCallback(async (missionId) => {
    try {
      setJoinFFALoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${missionId}/join-ffa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(
          selectedPurpose && selectedFaction
            ? { purpose: selectedPurpose, selectedFaction }
            : {}
        )
      });
      if (response.ok) {
        const updated = await response.json();
        onPurposeSelected(updated);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || t('warEffort.purposeSelection.failedToJoinGame'));
      }
    } catch (err) {
      console.error('Error joining FFA:', err);
        setError(t('warEffort.purposeSelection.failedToJoinGame'));
    } finally {
      setJoinFFALoading(false);
    }
  }, [selectedPurpose, selectedFaction, selectedChallenge, onPurposeSelected]);

  const handleAssignBot = useCallback(async () => {
    if (!mission?.id || !onMissionUpdate) return;
    try {
      setAssignBotLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${mission.id}/assign-bot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        onMissionUpdate(data);
        playSoundEffect('/sounds/click.mp3');
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || t('warEffort.purposeSelection.failedToAddBot'));
      }
    } catch (err) {
      console.error('Error assigning bot:', err);
        setError(t('warEffort.purposeSelection.failedToAddBot'));
    } finally {
      setAssignBotLoading(false);
    }
  }, [mission?.id, onMissionUpdate]);

  // FFA: create mission with purpose + faction only; players join open slots, then creator starts game
  const handleCreateFFAMission = useCallback(async () => {
    if (!selectedPurpose || !selectedFaction) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/select-purpose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purpose: selectedPurpose,
          selectedFaction,
          missionMode: 'ffa',
          neutralChallenge: selectedChallenge
        })
      });
      if (response.ok) {
        const mission = await response.json();
        onPurposeSelected(mission);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('warEffort.purposeSelection.failedToCreateFfa'));
      }
    } catch (err) {
      console.error('Error creating FFA mission:', err);
        setError(t('warEffort.purposeSelection.failedToCreateFfa'));
    } finally {
      setLoading(false);
    }
  }, [selectedPurpose, selectedFaction, selectedChallenge, onPurposeSelected]);

  const handleSelectSkeletonCrewClick = () => {
    if (!selectedPurpose || !selectedFaction) return;
    playSoundEffect('/sounds/click.mp3');
    setPhase('skeleton_crew');
    setError(null);
    onSkeletonCrewActiveChange?.(true);
  };

  const handleStartWithPendingCrew = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/select-purpose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ usePendingStrategyId: true })
      });
      if (response.ok) {
        const mission = await response.json();
        onPurposeSelected(mission);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('warEffort.purposeSelection.failedToStartMission'));
      }
    } catch (err) {
      console.error('Error starting mission with pending crew:', err);
      setError(t('warEffort.purposeSelection.failedToStartMission'));
    } finally {
      setLoading(false);
    }
  }, [onPurposeSelected]);

  useEffect(() => {
    return () => { onSkeletonCrewActiveChange?.(false); };
  }, [onSkeletonCrewActiveChange]);

  if (pendingLoading) {
    return (
      <div className="war-effort-purpose-selection">
        <p className="purpose-description" style={{ color: '#aaa' }}>{t('warEffort.purposeSelection.loading')}</p>
      </div>
    );
  }

  if (pendingSkeletonCrew) {
    const { purpose, selectedFaction: pendingFaction } = pendingSkeletonCrew;
    return (
      <div className="skeleton-crew-viewport-wrapper">
        <div className="skeleton-crew-fullscreen-background" style={SKELETON_CREW_BG_STYLE} />
        <div className="skeleton-crew-viewport-content">
          <div className="war-effort-purpose-selection war-effort-purpose-selection--locked">
            <h2>{t('warEffort.purposeSelection.continueWithSkeletonCrew')}</h2>
            <p className="purpose-description">
              {t('warEffort.purposeSelection.skeletonCrewReady')}
            </p>
            <div className="purpose-locked-display">
              <div className="purpose-locked-row">
                <span className="purpose-locked-label">{t('warEffort.purpose')}</span>
                <span className="purpose-locked-value">{purpose}</span>
              </div>
              <div className="purpose-locked-row">
                <span className="purpose-locked-label">{t('warEffort.purposeSelection.factionLabel')}</span>
                <span className="purpose-locked-value">{pendingFaction}</span>
              </div>
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="purpose-actions-row purpose-actions-row--locked">
              <button
                className="start-mission-button"
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  handleStartWithPendingCrew();
                }}
                disabled={loading}
              >
                {loading ? t('warEffort.purposeSelection.starting') : t('warEffort.purposeSelection.startMission')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'skeleton_crew') {
    return (
      <WarEffortSkeletonCrewSelection
        purpose={selectedPurpose}
        selectedFaction={selectedFaction}
        onComplete={handleSkeletonCrewComplete}
        onBack={() => setPhase(null)}
        isSubmitting={loading}
        submitError={error}
      />
    );
  }

  const showDevSimulateButton = isDevUser && hasExisting2v2Mission && typeof onDevSimulate2v2Join === 'function';
  const showDevResetButton = isDevUser && mission?.mission_mode === '2v2' && mission?.status !== 'waiting_ally' && typeof onDevResetToWaitingAlly === 'function';

  return (
    <div className="war-effort-purpose-selection" style={{ position: 'relative' }}>
      {/* Dev: Reset to waiting_ally (when status is stuck so Simulate can run) */}
      {showDevResetButton && (
        <button
          type="button"
          className="developer-mode-button"
          disabled={devResetLoading}
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            onDevResetToWaitingAlly();
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            padding: '0.5rem 1rem',
            marginLeft: 8,
            backgroundColor: 'rgba(120, 60, 0, 0.9)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 6,
            color: '#fff',
            cursor: devResetLoading ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}
        >
          {devResetLoading ? t('warEffort.purposeSelection.resetting') : t('warEffort.purposeSelection.devResetToWaitingAlly')}
        </button>
      )}
      {/* Dev: Simulate 3 players join = ally (player2) + opponent (player3) + opponent ally (player4); mission activates */}
      {showDevSimulateButton && (
        <button
          type="button"
          className="developer-mode-button"
          disabled={devSimulateLoading}
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            onDevSimulate2v2Join(mission?.id);
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: showDevResetButton ? 220 : 0,
            zIndex: 10,
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(180, 100, 0, 0.9)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 6,
            color: '#fff',
            cursor: devSimulateLoading ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}
        >
          {devSimulateLoading ? t('warEffort.purposeSelection.simulating') : t('warEffort.purposeSelection.devSimulate3Join')}
        </button>
      )}
      <h2>{t('warEffort.purposeSelection.selectYourWar')}</h2>
      <div className="mission-mode-row" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`purpose-button mission-mode-button ${missionMode === '1v1' ? 'selected' : ''}`}
          onClick={() => { playSoundEffect('/sounds/click.mp3'); setMissionMode('1v1'); setError(null); }}
          disabled={loading}
          style={{
            width: 80,
            height: 80,
            minWidth: 80,
            minHeight: 80,
            maxWidth: 80,
            maxHeight: 80,
            backgroundImage: `url(${(process.env.PUBLIC_URL || '')}/images/1v1.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="purpose-content">
            <div className="purpose-name">{t('warEffort.purposeSelection.mode1v1')}</div>
          </div>
        </button>
        <button
          type="button"
          className={`purpose-button mission-mode-button ${missionMode === '2v2' ? 'selected' : ''}`}
          onClick={() => { playSoundEffect('/sounds/click.mp3'); setMissionMode('2v2'); setError(null); }}
          disabled={loading}
          style={{
            width: 80,
            height: 80,
            minWidth: 80,
            minHeight: 80,
            maxWidth: 80,
            maxHeight: 80,
            backgroundImage: `url(${(process.env.PUBLIC_URL || '')}/images/2v2.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="purpose-content">
            <div className="purpose-name">{t('warEffort.purposeSelection.mode2v2')}</div>
          </div>
        </button>
        <button
          type="button"
          className={`purpose-button mission-mode-button ${missionMode === 'ffa' ? 'selected' : ''}`}
          onClick={() => { playSoundEffect('/sounds/click.mp3'); setMissionMode('ffa'); setError(null); }}
          disabled={loading}
          style={{
            width: 80,
            height: 80,
            minWidth: 80,
            minHeight: 80,
            maxWidth: 80,
            maxHeight: 80,
            backgroundImage: `url(${(process.env.PUBLIC_URL || '')}/images/ffa.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="purpose-content">
            <div className="purpose-name">{t('warEffort.purposeSelection.modeFfa')}</div>
          </div>
        </button>
      </div>
      <h2 style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>{t('warEffort.purposeSelection.selectYourChallenge')}</h2>
      <div className="mission-mode-row" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`purpose-button challenge-button ${selectedChallenge === 'easy' ? 'selected' : ''}`}
          onClick={() => { playSoundEffect('/sounds/click.mp3'); setSelectedChallenge('easy'); setError(null); }}
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem',
            minWidth: 160,
            minHeight: 100,
            background: 'transparent',
            backgroundImage: `url(${(process.env.PUBLIC_URL || '')}/images/strategy_easy.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: `2px solid ${selectedChallenge === 'easy' ? '#4caf50' : 'rgba(255,255,255,0.3)'}`,
            borderRadius: 8,
            color: '#eee',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          <div className="purpose-content">{t('warEffort.purposeSelection.challengeEasy')}</div>
        </button>
        <button
          type="button"
          className={`purpose-button challenge-button ${selectedChallenge === 'hard' ? 'selected' : ''}`}
          onClick={() => { playSoundEffect('/sounds/click.mp3'); setSelectedChallenge('hard'); setError(null); }}
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem',
            minWidth: 160,
            minHeight: 100,
            background: 'transparent',
            backgroundImage: `url(${(process.env.PUBLIC_URL || '')}/images/strategy_hard.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: `2px solid ${selectedChallenge === 'hard' ? '#f44336' : 'rgba(255,255,255,0.3)'}`,
            borderRadius: 8,
            color: '#eee',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          <div className="purpose-content">{t('warEffort.purposeSelection.challengeHard')}</div>
        </button>
      </div>
      {missionMode === '2v2' && (
        <>
          <div className="war-effort-2v2-slots" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem', position: 'relative' }}>
            {/* Slot 1: player (creator) — show player name, not "You" */}
            <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
              <div style={{ fontWeight: 600 }}>{mission?.player1_username || user || '—'}</div>
              <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                {[mission?.player1_purpose, mission?.player1_faction_name || mission?.player1_faction].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.slotOpen')}
              </div>
              {hasExisting2v2Mission && mission?.player1_ready && (
                <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
              )}
            </div>
            {/* Slot 2: ally — when empty show only "Open" (no title); when filled show real player name */}
            <div
              ref={allyPicklistRef}
              style={{
                padding: '0.5rem',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 6,
                textAlign: 'center',
                position: 'relative',
                cursor: canPickAlly ? 'pointer' : 'default',
                backgroundColor: canPickAlly && showAllyPicklist ? 'rgba(255,255,255,0.1)' : undefined
              }}
              onClick={() => canPickAlly && setShowAllyPicklist(prev => !prev)}
              role={canPickAlly ? 'button' : undefined}
              tabIndex={canPickAlly ? 0 : undefined}
              onKeyDown={e => canPickAlly && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setShowAllyPicklist(prev => !prev))}
            >
              {hasExisting2v2Mission && mission?.player2_id ? (
                <>
                  <div style={{ fontWeight: 600 }}>{mission.player2_username || '—'}</div>
                  <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                    {[mission.player2_purpose, mission.player2_faction_name].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.slotJoined')}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                  {mission?.player1_ally_invited_id
                    ? (friends?.find(f => Number(f.id) === Number(mission.player1_ally_invited_id))?.username || t('warEffort.purposeSelection.inviteSent'))
                    : t('warEffort.purposeSelection.slotOpen')}
                </div>
              )}
              {hasExisting2v2Mission && mission?.player2_ready && (
                <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
              )}
              {canPickAlly && showAllyPicklist && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    background: 'rgba(20,20,20,0.98)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 6,
                    maxHeight: 220,
                    overflowY: 'auto',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  {Array.isArray(friends) && friends.length > 0 ? (
                    friends.map((friend) => {
                      const id = Number(friend.id);
                      const alreadyInvited = Number(mission?.player1_ally_invited_id) === id;
                      const name = friend.username || friend.name || `Friend #${id}`;
                      return (
                        <div
                          key={id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (inviteAllyLoading || alreadyInvited) return;
                            playSoundEffect('/sounds/click.mp3');
                            onInviteAlly?.(id);
                            setShowAllyPicklist(false);
                          }}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!inviteAllyLoading && !alreadyInvited) { playSoundEffect('/sounds/click.mp3'); onInviteAlly?.(id); setShowAllyPicklist(false); } } }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: inviteAllyLoading || alreadyInvited ? 'default' : 'pointer',
                            color: alreadyInvited ? '#888' : '#eee',
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            fontSize: '0.9rem'
                          }}
                        >
                          {name}{alreadyInvited ? t('warEffort.purposeSelection.inviteSentSuffix') : ''}
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '0.75rem', color: '#aaa', fontSize: '0.9rem' }}>{t('warEffort.purposeSelection.noFriendsAddFromPanel')}</div>
                  )}
                </div>
              )}
            </div>
            {/* Slot 3: opponent 1 — when empty show only "Open"; when filled show real player name */}
            <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
              {hasExisting2v2Mission && mission?.player3_id ? (
                <>
                  <div style={{ fontWeight: 600 }}>{mission.player3_username || '—'}</div>
                  <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                    {[mission.player3_purpose, mission.player3_faction_name].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.slotJoined')}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('warEffort.purposeSelection.slotOpen')}</div>
              )}
              {hasExisting2v2Mission && mission?.player3_ready && (
                <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
              )}
            </div>
            {/* Slot 4: opponent 2 — when empty show only "Open"; when filled show real player name */}
            <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
              {hasExisting2v2Mission && mission?.player4_id ? (
                <>
                  <div style={{ fontWeight: 600 }}>{mission.player4_username || '—'}</div>
                  <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                    {[mission.player4_purpose, mission.player4_faction_name].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.slotJoined')}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('warEffort.purposeSelection.slotOpen')}</div>
              )}
              {hasExisting2v2Mission && mission?.player4_ready && (
                <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
              )}
            </div>
          </div>
          {hasExisting2v2Mission && effectiveIsPlayer1 && inviteAllyError && (
            <div style={{ color: '#f88', marginBottom: '0.5rem' }}>{inviteAllyError}</div>
          )}
          {hasExisting2v2Mission && mission?.status === 'waiting_opponent_ally' && !allSlotsFilledIn2v2 && (
            <p style={{ color: '#aaa', marginBottom: '1rem' }}>{t('warEffort.purposeSelection.waitingOpponentInviteAlly')}</p>
          )}
        </>
      )}
      {missionMode === 'ffa' && (
        <>
          {hasExistingFFAMission ? (
            <>
              <div className="war-effort-2v2-slots" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem', position: 'relative' }}>
                <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
                  <div style={{ fontWeight: 600 }}>{mission?.player1_username || user || '—'}</div>
                  <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                    {[mission?.player1_purpose, mission?.player1_faction_name || mission?.player1_faction].filter(Boolean).join(' • ') || (ffaMySlot === 1 ? t('warEffort.purposeSelection.selectBelow') : t('warEffort.purposeSelection.slotOpen'))}
                  </div>
                  {hasExistingFFAMission && mission?.player1_ready && (
                    <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
                  )}
                </div>
                <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
                  {hasExistingFFAMission && mission?.player2_id ? (
                    <>
                      <div style={{ fontWeight: 600 }}>{mission.player2_is_bot ? t('warEffort.purposeSelection.bot') : (mission.player2_username || '—')}</div>
                      <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{[mission.player2_purpose, mission.player2_faction_name || mission.player2_faction].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.selectBelow')}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('warEffort.purposeSelection.slotOpen')}</div>
                      {showAddBot && ffaFirstEmptySlot === 2 && (
                        <button type="button" onClick={handleAssignBot} disabled={assignBotLoading} style={{ marginTop: 6, padding: '0.25rem 0.5rem', fontSize: '0.8rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#ccc', cursor: assignBotLoading ? 'not-allowed' : 'pointer' }}>
                          {assignBotLoading ? t('warEffort.purposeSelection.addingBot') : t('warEffort.purposeSelection.addBot')}
                        </button>
                      )}
                    </>
                  )}
                  {hasExistingFFAMission && mission?.player2_ready && (
                    <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
                  )}
                </div>
                <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
                  {hasExistingFFAMission && mission?.player3_id ? (
                    <>
                      <div style={{ fontWeight: 600 }}>{mission.player3_is_bot ? t('warEffort.purposeSelection.bot') : (mission.player3_username || '—')}</div>
                      <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{[mission.player3_purpose, mission.player3_faction_name || mission.player3_faction].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.selectBelow')}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('warEffort.purposeSelection.slotOpen')}</div>
                      {showAddBot && ffaFirstEmptySlot === 3 && (
                        <button type="button" onClick={handleAssignBot} disabled={assignBotLoading} style={{ marginTop: 6, padding: '0.25rem 0.5rem', fontSize: '0.8rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#ccc', cursor: assignBotLoading ? 'not-allowed' : 'pointer' }}>
                          {assignBotLoading ? t('warEffort.purposeSelection.addingBot') : t('warEffort.purposeSelection.addBot')}
                        </button>
                      )}
                    </>
                  )}
                  {hasExistingFFAMission && mission?.player3_ready && (
                    <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
                  )}
                </div>
                <div className="war-effort-slot" style={{ padding: '0.5rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, textAlign: 'center' }}>
                  {hasExistingFFAMission && mission?.player4_id ? (
                    <>
                      <div style={{ fontWeight: 600 }}>{mission.player4_is_bot ? t('warEffort.purposeSelection.bot') : (mission.player4_username || '—')}</div>
                      <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{[mission.player4_purpose, mission.player4_faction_name || mission.player4_faction].filter(Boolean).join(' • ') || t('warEffort.purposeSelection.selectBelow')}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('warEffort.purposeSelection.slotOpen')}</div>
                      {showAddBot && ffaFirstEmptySlot === 4 && (
                        <button type="button" onClick={handleAssignBot} disabled={assignBotLoading} style={{ marginTop: 6, padding: '0.25rem 0.5rem', fontSize: '0.8rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#ccc', cursor: assignBotLoading ? 'not-allowed' : 'pointer' }}>
                          {assignBotLoading ? t('warEffort.purposeSelection.addingBot') : t('warEffort.purposeSelection.addBot')}
                        </button>
                      )}
                    </>
                  )}
                  {hasExistingFFAMission && mission?.player4_ready && (
                    <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: 700, marginTop: 4 }} title={t('warEffort.purposeSelection.ready')}>{t('warEffort.purposeSelection.readyCheckmark')}</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="open-ffa-games" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', margin: 0, color: '#ccc' }}>{t('warEffort.purposeSelection.openFfaGames')}</h3>
                <button
                  type="button"
                  onClick={() => { playSoundEffect('/sounds/click.mp3'); handleRefreshOpenFFA(); }}
                  disabled={openFFALoading}
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.85rem',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 6,
                    color: '#ccc',
                    cursor: openFFALoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {openFFALoading ? t('warEffort.purposeSelection.loading') : t('warEffort.purposeSelection.refresh')}
                </button>
              </div>
              {openFFALoading ? (
                <p style={{ color: '#888', fontSize: '0.9rem' }}>{t('warEffort.purposeSelection.loading')}</p>
              ) : openFFAMissions.length === 0 ? (
                <p style={{ color: '#888', fontSize: '0.9rem' }}>{t('warEffort.purposeSelection.noOpenFfaGames')}</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {openFFAMissions.map((m) => {
                    const count = [m.player1_id, m.player2_id, m.player3_id, m.player4_id].filter(Boolean).length;
                    const creatorName = m.player1_username || t('warEffort.purposeSelection.creator');
                    const takenPurposes = [m.player1_purpose, m.player2_purpose, m.player3_purpose, m.player4_purpose].filter(Boolean).map(p => (p || '').toUpperCase());
                    const purposeTaken = selectedPurpose && takenPurposes.includes(selectedPurpose.toUpperCase());
                    return (
                      <li
                        key={m.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 6,
                          marginBottom: '0.5rem',
                          background: 'rgba(255,255,255,0.05)'
                        }}
                      >
                        <span style={{ fontSize: '0.9rem', color: '#ddd' }}>
                          <strong>{creatorName}</strong> — {t('warEffort.purposeSelection.playersCount', { count })}
                        </span>
                        <button
                          type="button"
                          className="start-mission-button"
                          style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                          disabled={joinFFALoading || purposeTaken}
                          onClick={() => { playSoundEffect('/sounds/click.mp3'); handleJoinFFA(m.id); }}
                        >
                          {joinFFALoading ? t('warEffort.purposeSelection.joining') : t('warEffort.purposeSelection.join')}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.5rem' }}>{t('warEffort.purposeSelection.clickJoinHint')}</p>
            </div>
          )}
        </>
      )}
      <h2 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>{t('warEffort.purposeSelection.selectYourPurpose')}</h2>
      <div className="purpose-buttons">
        {PURPOSES.map(purpose => {
          const takenByOther = (missionMode === '2v2' && hasExisting2v2Mission && [
            mission?.player2_purpose, mission?.player3_purpose, mission?.player4_purpose
          ].some(p => p && p.toUpperCase() === purpose.toUpperCase())) ||
          (missionMode === 'ffa' && hasExistingFFAMission && [
            mission?.player1_purpose, mission?.player2_purpose, mission?.player3_purpose, mission?.player4_purpose
          ].some(p => p && p.toUpperCase() === purpose.toUpperCase()));
          return (
            <button
              key={purpose}
              className={`purpose-button ${selectedPurpose === purpose ? 'selected' : ''} ${takenByOther ? 'taken' : ''}`}
              onClick={() => !takenByOther && handlePurposeSelect(purpose)}
              disabled={loading || takenByOther}
              style={{
                backgroundImage: `url(/images/${PURPOSE_IMAGES[purpose]})`,
                ...(takenByOther ? { opacity: 0.35, filter: 'grayscale(0.8)', cursor: 'not-allowed' } : {})
              }}
            >
              <div className="purpose-content">
                <div className="purpose-name">{purpose}</div>
                <div className="purpose-factions">
                  {PURPOSE_TO_FACTION_MAPPING[purpose].join(', ')}
                </div>
                {takenByOther && (
                  <div style={{ fontSize: '0.7rem', color: '#ff8888', marginTop: 2, fontWeight: 600 }}>{t('warEffort.purposeSelection.alreadyChosen')}</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {selectedPurpose && missionMode === '1v1' && (
        <div className="purpose-selected-description">
          <p className="purpose-selected-text">{PURPOSE_DESCRIPTIONS[selectedPurpose]}</p>
        </div>
      )}
      {error && (
        <div className="error-message">{error}</div>
      )}
      {selectedPurpose && (
        <div className="purpose-actions-row purpose-actions-row--stacked">
          <div className="purpose-faction-bar">
            <div className="purpose-faction-icons">
              {PURPOSE_TO_FACTION_MAPPING[selectedPurpose].map(faction => (
                <div
                  key={faction}
                  role="button"
                  tabIndex={0}
                  onClick={async () => {
                    if (loading || setFFAPurposeLoading) return;
                    playSoundEffect('/sounds/click.mp3');
                    setSelectedFaction(faction);
                    if (hasExistingFFAMission && ffaMySlot != null && mission?.id) {
                      setSetFFAPurposeLoading(true);
                      setError(null);
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API_URL}/war-effort/mission/${mission.id}/set-ffa-purpose`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ purpose: selectedPurpose, selectedFaction: faction })
                        });
                        if (response.ok) {
                          const updated = await response.json();
                          onPurposeSelected(updated);
                        } else {
                          const errData = await response.json().catch(() => ({}));
                          setError(errData.error || t('warEffort.purposeSelection.failedToSetPurpose'));
                        }
                      } catch (err) {
                        console.error('Error setting FFA purpose:', err);
                        setError('Failed to set purpose');
                      } finally {
                        setSetFFAPurposeLoading(false);
                      }
                    }
                  }}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ' ') && !loading && !setFFAPurposeLoading) {
                      e.preventDefault();
                      playSoundEffect('/sounds/click.mp3');
                      setSelectedFaction(faction);
                      if (hasExistingFFAMission && ffaMySlot != null && mission?.id) {
                        const token = localStorage.getItem('token');
                        setSetFFAPurposeLoading(true);
                        setError(null);
                        fetch(`${API_URL}/war-effort/mission/${mission.id}/set-ffa-purpose`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ purpose: selectedPurpose, selectedFaction: faction })
                        })
                          .then(res => res.ok ? res.json() : res.json().then(d => { throw new Error(d.error || 'Failed'); }))
                          .then(updated => { onPurposeSelected(updated); })
                          .catch(err => setError(err.message || t('warEffort.purposeSelection.failedToSetPurpose')))
                          .finally(() => setSetFFAPurposeLoading(false));
                      }
                    }
                  }}
                  title={faction}
                  className={`purpose-faction-item ${selectedFaction === faction ? 'selected' : ''}`}
                >
                  <div
                    className="purpose-faction-orb"
                    style={{ background: getFactionOrbBackground(faction) }}
                    aria-hidden
                  />
                  <span className="purpose-faction-name">{faction}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Buttons below faction selector (bottom of interface) */}
          <div className="purpose-actions-buttons">
            {missionMode === '1v1' && (
              <button
                className="start-mission-button"
                onClick={handleSelectSkeletonCrewClick}
                disabled={loading || !selectedFaction}
              >
                {t('warEffort.purposeSelection.selectSkeletonCrew')}
              </button>
            )}
            {missionMode === '2v2' && !hasExisting2v2Mission && (
              <button
                className="start-mission-button"
                onClick={() => { playSoundEffect('/sounds/click.mp3'); handleCreate2v2Mission(); }}
                disabled={loading || !selectedFaction}
              >
                {loading ? t('warEffort.purposeSelection.creating') : t('warEffort.purposeSelection.create2v2Mission')}
              </button>
            )}
            {missionMode === 'ffa' && !hasExistingFFAMission && (
              <button
                className="start-mission-button"
                onClick={() => { playSoundEffect('/sounds/click.mp3'); handleCreateFFAMission(); }}
                disabled={loading || !selectedFaction}
              >
                {loading ? t('warEffort.purposeSelection.creating') : t('warEffort.purposeSelection.createFfaMission')}
              </button>
            )}
            {/* Same ready flow as 2v2: Ready button or "You are ready" when not all ready; host sees "Select Skeleton Crew" when all ready */}
            {showReadySection && (
              !viewerIsReady ? (
                <button
                  className="start-mission-button"
                  onClick={() => { playSoundEffect('/sounds/click.mp3'); onReady?.(); }}
                  disabled={loading || !selectedPurpose || !selectedFaction}
                  style={{ padding: '0.6rem 2rem', fontSize: '1rem', fontWeight: 700 }}
                >
                  {t('warEffort.purposeSelection.ready')}
                </button>
              ) : (
                <p style={{ color: '#4caf50', fontWeight: 600, margin: 0 }}>{t('warEffort.purposeSelection.youAreReady')}</p>
              )
            )}
            {allReady && effectiveIsPlayer1 && (
              <>
                <p style={{ color: '#4caf50', fontWeight: 600, marginBottom: '0.5rem' }}>{t('warEffort.purposeSelection.allPlayersReady')}</p>
                <button
                  className="start-mission-button"
                  onClick={() => { playSoundEffect('/sounds/click.mp3'); onStartCrewSelection?.(); }}
                  style={{ padding: '0.75rem 2.5rem', fontSize: '1.1rem', fontWeight: 700 }}
                >
                  {t('warEffort.purposeSelection.selectSkeletonCrew')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showTutorialPurposeSelection && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_explain_4.png"
          text={t('warEffort.purposeSelectionTutorialText')}
          onContinue={async () => {
            playSoundEffect('/sounds/click.mp3');
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'war_effort_purpose_selection' }),
              });
              markSeenLocally('war_effort_purpose_selection');
            }
            setShowTutorialPurposeSelection(false);
          }}
          voiceKey="warEffort.purposeSelectionTutorialText"
        />,
        document.body
      )}
    </div>
  );
}

export default WarEffortPurposeSelection;
